import { parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Espelho TS do EvoTalksPhoneUtils do geovendas-service (normalizarTelefone +
 * formatarParaExibicao) — MANTER IDENTICO ao backend. Usado para copiar o numero
 * do contato exatamente na forma em que ele esta gravado no EvoTalks
 * ("55 47 99988-7766"; internacionais no formato do pais pela libphonenumber).
 */
export class EvoTalksPhoneHelper {
  /**
   * Forma canonica: BR = 55 + DDD + 9 + 8 digitos; internacional = digitos com DDI
   * preservado (E.164 8-15). Invalido (ramal, incompleto, lixo) retorna null.
   *
   * `origemCadastro` = numero vindo do cadastro do GEO (convencao: sem DDI), onde 10 digitos com
   * o local em 6-9 e celular legado pre-nono-digito. Sem a flag vale o contrato do webhook, em
   * que o DDI vem sempre e 10 digitos sao internacionais de DDI curto.
   */
  static normalizarTelefone(telefone: string | null | undefined, origemCadastro = false): string | null {
    if (!telefone || !telefone.trim()) {
      return null;
    }
    if (telefone.includes('@lid')) {
      return null;
    }
    const ddiExplicito = telefone.trim().startsWith('+');
    let digits = telefone.replace(/\D/g, '');
    if (!digits) {
      return null;
    }
    if (ddiExplicito && !digits.startsWith('55')) {
      return digits.length >= 8 && digits.length <= 15 ? digits : null;
    }
    if (digits.length === 10 || digits.length === 11) {
      const primeiroDigitoLocal = digits.charAt(2);
      const limiteLocal = origemCadastro ? '9' : '5';
      const padraoBr = (digits.length === 11 && primeiroDigitoLocal === '9')
        || (digits.length === 10 && primeiroDigitoLocal >= '2' && primeiroDigitoLocal <= limiteLocal);
      if (padraoBr) {
        digits = `55${digits}`;
      } else {
        return digits;
      }
    }
    if (!digits.startsWith('55')) {
      return digits.length >= 11 && digits.length <= 15 ? digits : null;
    }
    if (digits.length === 14 && digits.charAt(4) === '9' && digits.charAt(5) === '9') {
      digits = digits.substring(0, 4) + digits.substring(5);
    }
    if (digits.length === 12) {
      digits = `${digits.substring(0, 4)}9${digits.substring(4)}`;
    }
    return digits.length === 13 && digits.charAt(4) === '9' ? digits : null;
  }

  /** Forma de leitura do EvoTalks: espacos e hifen, sem '+' ou parenteses. */
  static formatarParaExibicao(numero: string | null | undefined): string {
    if (!numero || !numero.trim()) {
      return '';
    }
    const digits = numero.replace(/\D/g, '');
    if (digits.startsWith('55')) {
      if (digits.length === 13) {
        return `55 ${digits.substring(2, 4)} ${digits.substring(4, 9)}-${digits.substring(9)}`;
      }
      if (digits.length === 12) {
        return `55 ${digits.substring(2, 4)} ${digits.substring(4, 8)}-${digits.substring(8)}`;
      }
    }
    return EvoTalksPhoneHelper.formatarInternacionalParaExibicao(digits);
  }

  /**
   * Internacionais (nao-BR) formatados pela libphonenumber no padrao do pais, sem '+' nem
   * parenteses e com hifen trocado por espaco (ex.: "1 415 555 2671") — mesma normalizacao do
   * backend Java. Numero que a lib nao reconhece como valido fica so com os digitos.
   *
   * LIMITE CONHECIDO: a saida NAO e garantidamente igual a do backend em todo DDI. A
   * libphonenumber-js e a libphonenumber Java trazem metadata proprio e discordam do
   * agrupamento/validade em alguns paises (ex.: Noruega, "+47 99887766" -> aqui
   * "47 99 88 77 66", no backend "47 998 87 766"). Para BR — a esmagadora maioria — a
   * formatacao e local e identica nos dois lados.
   */
  private static formatarInternacionalParaExibicao(digits: string): string {
    try {
      const parsed = parsePhoneNumberFromString(`+${digits}`);
      if (parsed && parsed.isValid()) {
        return parsed.formatInternational()
          .replace(/[+()]/g, '')
          .replace(/-/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      }
    } catch {
      // sem DDI reconhecivel/parse falhou: cai para os digitos
    }
    return digits;
  }

  /**
   * Numero como o sync de CONTATO grava no EvoTalks: normaliza e formata. Se a normalizacao
   * rejeitar (numero invalido, que nem sincroniza), devolve so os digitos crus.
   */
  static formatoEvoTalks(telefone: string | null | undefined): string {
    // o numero vem do cadastro do contato (crmcontatos.whatsapp), nao do webhook
    const canonico = EvoTalksPhoneHelper.normalizarTelefone(telefone, true);
    if (canonico) {
      return EvoTalksPhoneHelper.formatarParaExibicao(canonico);
    }
    return telefone ? telefone.replace(/\D/g, '') : '';
  }

  /**
   * Numero como o sync de EMPRESA grava no EvoTalks (espelho de
   * EvoTalksPhoneUtils.formatarTelefoneEmpresa): ganha o DDI 55 quando o padrao local bate, mas
   * NAO ganha o nono digito — telefone de cliente/prospect nao passa pelo canonico de WhatsApp.
   */
  static formatoEvoTalksEmpresa(telefone: string | null | undefined): string {
    if (!telefone || !telefone.trim()) {
      return '';
    }
    let digits = telefone.replace(/\D/g, '');
    if (!digits) {
      return '';
    }
    const internacionalExplicito = telefone.trim().startsWith('+') && !digits.startsWith('55');
    if (!internacionalExplicito) {
      const primeiroDigitoLocal = digits.charAt(2);
      if (digits.length === 11 && primeiroDigitoLocal === '9') {
        digits = `55${digits}`;
      } else if (digits.length === 10 && primeiroDigitoLocal >= '2' && primeiroDigitoLocal <= '9') {
        digits = `55${digits}`;
      }
    }
    return EvoTalksPhoneHelper.formatarParaExibicao(digits);
  }
}
